import React, { useCallback, useEffect, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { notify } from '../../components/common/AppToast';
import KkpPlanningEmptyState from '../../components/kkp/KkpPlanningEmptyState';
import {
  AppButton,
  AppFormField,
  AppInput,
  AppModal,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import kkpDistribService from '../../services/kkpDistribService';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-distrib-page.scss';

const EMPTY = { label: '', livreur: '', couleur: '#64748B', ordre: 0 };

const KkpDistribZonesPage = () => {
  const { ask, modal } = useAppConfirm();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await kkpDistribService.listZones();
      setZones(list);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, ordre: zones.length + 1 });
    setModalOpen(true);
  };

  const openEdit = (zone) => {
    setEditing(zone);
    setForm({
      label: zone.label || '',
      livreur: zone.livreur || '',
      couleur: zone.couleur || '#64748B',
      ordre: zone.ordre ?? 0,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) {
        await kkpDistribService.updateZone(editing.id, form);
        notify.success('Zone mise à jour');
      } else {
        await kkpDistribService.createZone(form);
        notify.success('Zone créée');
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zone) => {
    const ok = await ask({
      title: 'Archiver cette zone ?',
      message: `Archiver la zone « ${zone.label} » ?`,
      confirmLabel: 'Archiver',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await kkpDistribService.deleteZone(zone.id);
      notify.success('Zone archivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Zones de distribution"
        subtitle="INT, EXT, Morvan… chaque zone a un livreur et une couleur carte"
        actions={(
          <AppButton icon="mdi:plus" onClick={openCreate}>
            Nouvelle zone
          </AppButton>
        )}
      />

      {loading ? (
        <KkpPlanningEmptyState loading />
      ) : zones.length === 0 ? (
        <KkpPlanningEmptyState
          message="Aucune zone. Créez INT, EXT ou une tournée personnalisée."
          actions={[{ key: 'create', label: 'Nouvelle zone', icon: 'mdi:plus', variant: 'primary', onClick: openCreate }]}
        />
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Couleur</th>
                  <th>Zone</th>
                  <th>Code</th>
                  <th>Livreur</th>
                  <th>Ordre</th>
                  <th>Points</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {zones.map((zone) => (
                  <tr key={zone.id}>
                    <td>
                      <span className="kkp-distrib-zone-swatch" style={{ background: zone.couleur }} />
                    </td>
                    <td className="fw-semibold">{zone.label}</td>
                    <td><code>{zone.code}</code></td>
                    <td>{zone.livreur || '—'}</td>
                    <td>{zone.ordre}</td>
                    <td>{zone.points}</td>
                    <td className="text-nowrap text-end">
                      <AppButton
                        size="sm"
                        variant="outline-secondary"
                        className="me-1"
                        icon="mdi:pencil-outline"
                        onClick={() => openEdit(zone)}
                      />
                      <AppButton
                        size="sm"
                        variant="outline-danger"
                        icon="mdi:archive-outline"
                        onClick={() => handleDelete(zone)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AppModal
        show={modalOpen}
        title={editing ? 'Modifier la zone' : 'Nouvelle zone'}
        onClose={() => setModalOpen(false)}
        contentAs="form"
        contentProps={{ onSubmit: handleSubmit }}
        footer={(
          <>
            <AppButton variant="outline-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Annuler</AppButton>
            <AppButton type="submit" loading={saving} loadingLabel="Enregistrement…" disabled={saving || !form.label.trim()}>
              Enregistrer
            </AppButton>
          </>
        )}
      >
        <AppFormField label="Nom" htmlFor="zone-label" required>
          <AppInput
            id="zone-label"
            required
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          />
        </AppFormField>
        <AppFormField label="Livreur" htmlFor="zone-livreur">
          <AppInput
            id="zone-livreur"
            value={form.livreur}
            onChange={(e) => setForm((f) => ({ ...f, livreur: e.target.value }))}
          />
        </AppFormField>
        <div className="row g-3">
          <AppFormField className="col-6" label="Couleur carte" htmlFor="zone-couleur">
            <AppInput
              id="zone-couleur"
              type="color"
              className="form-control-color"
              value={form.couleur}
              onChange={(e) => setForm((f) => ({ ...f, couleur: e.target.value }))}
            />
          </AppFormField>
          <AppFormField className="col-6" label="Ordre" htmlFor="zone-ordre">
            <AppInput
              id="zone-ordre"
              type="number"
              min="0"
              value={form.ordre}
              onChange={(e) => setForm((f) => ({ ...f, ordre: Number(e.target.value) || 0 }))}
            />
          </AppFormField>
        </div>
      </AppModal>
      {modal}
    </MasterLayout>
  );
};

export default KkpDistribZonesPage;
